import { useMemo } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import {
    faArrowRight,
    faBookOpenReader,
    faCalendar,
    faCheck,
    faMoneyBill,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCreateCampaignDialogContext } from 'components/create-campaign-dialog/CreateCampaignDialogContext';
import { ComparisonFeature, features } from 'components/create-campaign-dialog/constants';
import { EmptyData } from 'components/util';
import { CAMPAIGN_HELP_CREATE_TYPES_LINK } from 'constants/links';
import { twMerge } from 'tailwind-merge';

const columnHelper = createColumnHelper<ComparisonFeature>();

const ComparisonList = () => {
    const { setStep } = useCreateCampaignDialogContext();
    const isMobile = useMediaQuery(`(max-width: 640px)`);

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                header: '',
                cell: info => info.getValue(),
            }),
            columnHelper.accessor('mobileName', {
                header: '',
                cell: info => info.getValue() ?? info.row.getValue('name'),
            }),
            columnHelper.accessor('donationForm', {
                header: () => (
                    <span className="flex flex-col items-center justify-center gap-2 sm:flex-row">
                        <div
                            className={
                                'flex size-8 items-center justify-center rounded-md bg-gray-100 [&>svg]:text-gray-500'
                            }
                        >
                            <FontAwesomeIcon icon={faMoneyBill} />
                        </div>
                        {isMobile ? 'Form' : 'Donation form'}
                    </span>
                ),
                cell: info =>
                    info.getValue() ? (
                        <FontAwesomeIcon
                            title="checkmark"
                            icon={faCheck}
                            className="mx-auto text-green-500"
                        />
                    ) : (
                        <EmptyData />
                    ),
            }),
            columnHelper.accessor('fundraisingPage', {
                header: () => (
                    <span className="flex flex-col items-center justify-center gap-2 sm:flex-row">
                        <div
                            className={
                                'flex size-8 shrink-0 items-center justify-center rounded-md bg-gray-100 [&>svg]:text-gray-500'
                            }
                        >
                            <FontAwesomeIcon icon={faBookOpenReader} />
                        </div>
                        {isMobile ? 'Page' : 'Fundraising page'}
                    </span>
                ),
                cell: info =>
                    info.getValue() ? (
                        <FontAwesomeIcon
                            title="checkmark"
                            icon={faCheck}
                            className="mx-auto text-green-500"
                        />
                    ) : (
                        <EmptyData />
                    ),
            }),
            columnHelper.accessor('event', {
                header: () => (
                    <span className="flex flex-col items-center justify-center gap-2 sm:flex-row">
                        <div
                            className={
                                'flex size-8 items-center justify-center rounded-md bg-gray-100 [&>svg]:text-gray-500'
                            }
                        >
                            <FontAwesomeIcon icon={faCalendar} />
                        </div>
                        Event
                    </span>
                ),
                cell: info =>
                    info.getValue() ? (
                        <FontAwesomeIcon
                            title="checkmark"
                            icon={faCheck}
                            className="mx-auto text-green-500"
                        />
                    ) : (
                        <EmptyData />
                    ),
            }),
        ],
        [isMobile],
    );

    const table = useReactTable({
        data: features,
        columns,
        pageCount: -1,
        state: {
            columnVisibility: {
                name: !isMobile,
                mobileName: !!isMobile,
            },
        },
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className="flex min-h-full items-center justify-center">
            <div
                className={
                    'flex h-auto w-full flex-col items-center justify-center gap-4 p-4 sm:px-12'
                }
            >
                <Button
                    variant="outline"
                    color="neutral"
                    className={isMobile ? 'mr-auto' : 'absolute left-4 top-4 z-10'}
                    onClick={() => setStep('start')}
                >
                    Back
                </Button>

                <table className={`w-full table-fixed border-collapse sm:table-auto`}>
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className={
                                            'w-16 px-3 py-4 text-center font-medium text-gray-900 first:w-full sm:w-auto sm:p-4 sm:first:w-1/4'
                                        }
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext(),
                                              )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="overflow-hidden rounded-xl shadow-md outline outline-1 outline-gray-300">
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id} className="h-14 even:bg-gray-100 sm:h-12">
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        className={twMerge(
                                            'border-r text-center last:border-r-0 sm:w-1/4',
                                            row.index === table.getRowModel().rows.length - 1 &&
                                                'first:rounded-bl-xl last:rounded-br-xl',
                                            (cell.column.id === 'name' ||
                                                cell.column.id === 'mobileName') &&
                                                'w-full px-5 text-left text-sm text-gray-900 sm:w-1/4 sm:px-6',
                                        )}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Button
                    size="sm"
                    variant={'solid'}
                    color={'neutral'}
                    className="rounded-full"
                    onClick={() => window.open(CAMPAIGN_HELP_CREATE_TYPES_LINK, '_blank')}
                >
                    Still need more info?
                    <span className="text-brand-cobalt-primary flex items-center gap-1">
                        View help center
                        <FontAwesomeIcon className="size-3" icon={faArrowRight} />
                    </span>
                </Button>
            </div>
        </div>
    );
};

export { ComparisonList };
